import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API || "https://www.vilaro.ro/api";

const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" };
const btnPrimary = { background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white" };
const btnGray = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" };

const classificationStyles = {
  sustinator: { background: "rgba(34,197,94,0.15)", color: "#86efac" },
  critic: { background: "rgba(234,179,8,0.15)", color: "#fde047" },
  troll: { background: "rgba(239,68,68,0.15)", color: "#fca5a5" },
  intrebare: { background: "rgba(99,102,241,0.15)", color: "#a5b4fc" },
  neutru: { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" },
};

const pageTypeStyles = {
  official: { background: "rgba(99,102,241,0.15)", color: "#a5b4fc" },
  support: { background: "rgba(34,197,94,0.15)", color: "#86efac" },
  thematic: { background: "rgba(139,92,246,0.15)", color: "#c4b5fd" },
  reaction: { background: "rgba(239,68,68,0.15)", color: "#fca5a5" },
};

const pageTypeColors = { official: "#a5b4fc", support: "#86efac", thematic: "#c4b5fd", reaction: "#fca5a5" };
const pageTypeIcons = { official: "📢", support: "👥", thematic: "📰", reaction: "⚔️" };

const toLocal = (dateStr) => {
  if (!dateStr) return "";
  const str = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  return new Date(str).toLocaleString();
};

function AIAnalysisModal({ chartType, data, context, title, onClose, children }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const analyze = async () => {
      try {
        const res = await axios.post(`${API}/stats/analyze-chart`, { chart_type: chartType, data, context: context || "" });
        if (res.data.error) setError(res.data.error);
        else setAnalysis(res.data.analysis);
      } catch (e) { setError("Eroare la conectarea cu AI"); }
      finally { setLoading(false); }
    };
    analyze();
  }, [chartType]);
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: "rgba(0,0,0,0.85)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rounded-2xl w-full max-w-2xl max-h-screen overflow-y-auto" style={{ background: "#0f0f1a", border: "1px solid rgba(99,102,241,0.3)" }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-2"><span className="text-lg">🤖</span><h3 className="font-semibold text-white">{title}</h3></div>
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-xl" style={btnGray}>✕ Închide</button>
        </div>
        <div className="p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>{children}</div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-1.5 h-1.5 rounded-full" style={{ background: "#6366f1" }}></div><span className="text-xs font-medium" style={{ color: "#a5b4fc" }}>Analiză AI</span></div>
          {loading && <div className="flex items-center gap-3 py-4"><div className="w-4 h-4 rounded-full border-2 animate-spin" style={{ borderColor: "#6366f1", borderTopColor: "transparent" }}></div><span className="text-sm" style={{ color: "rgba(255,255,255,0.4)" }}>Se generează analiza...</span></div>}
          {error && <div className="rounded-xl p-4 text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#fca5a5" }}>{error}</div>}
          {analysis && <div className="rounded-xl p-4 text-sm leading-relaxed" style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)", color: "rgba(255,255,255,0.85)" }}>{analysis}</div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="rounded-2xl p-5" style={card}>
      <p className="text-xs mb-1" style={{ color: "rgba(255,255,255,0.4)" }}>{label}</p>
      <p className="text-3xl font-bold" style={{ color: color || "white" }}>{value}</p>
    </div>
  );
}

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 rounded-full h-1.5" style={{ background: "rgba(255,255,255,0.06)" }}>
        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color || "#6366f1" }}></div>
      </div>
      <span className="text-xs w-8 text-right" style={{ color: "rgba(255,255,255,0.4)" }}>{value}</span>
    </div>
  );
}

function SentimentBar({ positive, negative, trolls, total }) {
  if (!total || total === 0) return null;
  const posP = Math.round((positive / total) * 100);
  const negP = Math.round((negative / total) * 100);
  const trlP = Math.round((trolls / total) * 100);
  return (
    <div>
      <div className="flex rounded-full overflow-hidden h-2 mb-1">
        <div style={{ width: `${posP}%`, background: "#86efac" }}></div>
        <div style={{ width: `${negP}%`, background: "#fde047" }}></div>
        <div style={{ width: `${trlP}%`, background: "#fca5a5" }}></div>
        <div style={{ flex: 1, background: "rgba(255,255,255,0.06)" }}></div>
      </div>
      <div className="flex gap-3 text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
        <span style={{ color: "#86efac" }}>✓ {posP}%</span>
        <span style={{ color: "#fde047" }}>⚠ {negP}%</span>
        <span style={{ color: "#fca5a5" }}>✗ {trlP}%</span>
      </div>
    </div>
  );
}

function PieChart({ data, size = 120, onClick }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div className="flex items-center justify-center" style={{ height: size, color: "rgba(255,255,255,0.2)", fontSize: 12 }}>Nu există date</div>;
  let cumulative = 0;
  const slices = data.filter(d => d.value > 0).map(d => {
    const pct = d.value / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const r = size / 2 - 4; const cx = size / 2; const cy = size / 2;
    const x1 = cx + r * Math.cos(startAngle); const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle); const y2 = cy + r * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;
    return { ...d, path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`, pct: Math.round(pct * 100) };
  });
  const clickable = !!onClick;
  return (
    <div className={`flex items-center gap-4 ${clickable ? "cursor-pointer group" : ""}`} onClick={onClick} title={clickable ? "Click pentru analiză AI" : ""}>
      <div className="relative">
        <svg width={size} height={size}>{slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity={0.85} />)}</svg>
        {clickable && <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full" style={{ background: "rgba(99,102,241,0.3)" }}><span className="text-lg">🤖</span></div>}
      </div>
      <div className="space-y-1.5">
        {slices.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }}></div>
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{s.label}</span>
            <span className="text-xs font-medium" style={{ color: s.color }}>{s.pct}%</span>
          </div>
        ))}
        {clickable && <p className="text-xs mt-1" style={{ color: "rgba(99,102,241,0.6)" }}>🤖 click pentru analiză</p>}
      </div>
    </div>
  );
}

function BarChart({ data, valueKey = "value", labelKey = "label", color = "#6366f1", height = 120, onClick }) {
  if (!data || data.length === 0) return <div className="flex items-center justify-center text-xs" style={{ height, color: "rgba(255,255,255,0.2)" }}>Nu există date</div>;
  const max = Math.max(...data.map(d => d[valueKey] || 0));
  const clickable = !!onClick;
  return (
    <div className={clickable ? "cursor-pointer" : ""} onClick={onClick} title={clickable ? "Click pentru analiză AI" : ""}>
      <div className="flex items-end gap-1" style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>{d[valueKey] > 0 ? d[valueKey] : ""}</span>
            <div className="w-full rounded-t-sm transition-all" title={`${d[labelKey]}: ${d[valueKey]}`} style={{ height: `${max > 0 ? ((d[valueKey] || 0) / max) * (height - 30) : 0}px`, background: d.color || `linear-gradient(180deg, ${color}, ${color}99)`, minHeight: (d[valueKey] || 0) > 0 ? "4px" : "0" }}></div>
            <span className="text-xs truncate w-full text-center" style={{ color: "rgba(255,255,255,0.3)", fontSize: 9 }}>{d[labelKey]}</span>
          </div>
        ))}
      </div>
      {clickable && <p className="text-xs mt-2 text-center" style={{ color: "rgba(99,102,241,0.6)" }}>🤖 click pentru analiză</p>}
    </div>
  );
}

function StackedDailyChart({ data, height = 130, onClick }) {
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length === 0) return <div className="flex items-center justify-center text-xs" style={{ height, color: "rgba(255,255,255,0.2)" }}>Nu există date</div>;
  const max = Math.max(...data.map(d => (d.total || 0)));
  const barH = height - 28;
  const step = data.length > 20 ? 5 : data.length > 10 ? 3 : 1;
  const clickable = !!onClick;
  return (
    <div style={{ position: "relative" }} className={clickable ? "cursor-pointer" : ""} onClick={onClick}>
      {tooltip && (
        <div className="absolute z-10 rounded-xl px-3 py-2 text-xs pointer-events-none" style={{ background: "rgba(15,15,25,0.95)", border: "1px solid rgba(255,255,255,0.15)", top: -75, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
          <p className="font-medium text-white mb-1">{tooltip.day}</p>
          <p style={{ color: "#86efac" }}>✓ Susținători: {tooltip.positive || 0}</p>
          <p style={{ color: "#fde047" }}>⚠ Critici: {tooltip.negative || 0}</p>
          <p style={{ color: "#fca5a5" }}>✗ Troli: {tooltip.trolls || 0}</p>
          <p style={{ color: "rgba(255,255,255,0.5)" }}>Total: {tooltip.total || 0}</p>
        </div>
      )}
      <div className="flex items-end gap-0.5" style={{ height: barH }}>
        {data.map((d, i) => {
          const total = d.total || 0; const pos = d.positive || 0; const neg = d.negative || 0; const trl = d.trolls || 0;
          const totalH = max > 0 ? (total / max) * (barH - 4) : 0;
          const posH = total > 0 ? (pos / total) * totalH : 0;
          const negH = total > 0 ? (neg / total) * totalH : 0;
          const trlH = total > 0 ? (trl / total) * totalH : 0;
          const neuH = Math.max(0, totalH - posH - negH - trlH);
          return (
            <div key={i} className="flex-1 flex flex-col justify-end" style={{ height: barH }} onMouseEnter={e => { e.stopPropagation(); setTooltip(d); }} onMouseLeave={() => setTooltip(null)}>
              {total > 0 && (
                <div className="w-full flex flex-col rounded-t-sm overflow-hidden">
                  {neuH > 0 && <div style={{ height: neuH, background: "rgba(255,255,255,0.12)" }}></div>}
                  {trlH > 0 && <div style={{ height: trlH, background: "#fca5a5", opacity: 0.85 }}></div>}
                  {negH > 0 && <div style={{ height: negH, background: "#fde047", opacity: 0.85 }}></div>}
                  {posH > 0 && <div style={{ height: posH, background: "#86efac", opacity: 0.85 }}></div>}
                </div>
              )}
              {total === 0 && <div className="w-full rounded-t-sm" style={{ height: 2, background: "rgba(255,255,255,0.04)" }}></div>}
            </div>
          );
        })}
      </div>
      <div className="flex gap-0.5 mt-1" style={{ height: 16 }}>
        {data.map((d, i) => {
          const dayNum = d.day ? new Date(d.day).getDate() : null;
          const show = dayNum !== null && (i === 0 || i === data.length - 1 || dayNum % step === 0);
          return <div key={i} className="flex-1 text-center" style={{ fontSize: 8, color: show ? "rgba(255,255,255,0.35)" : "transparent" }}>{show ? dayNum : "·"}</div>;
        })}
      </div>
      <div className="flex gap-3 mt-2 text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
        <span style={{ color: "#86efac" }}>■ Susținători</span>
        <span style={{ color: "#fde047" }}>■ Critici</span>
        <span style={{ color: "#fca5a5" }}>■ Troli</span>
        <span style={{ color: "rgba(255,255,255,0.2)" }}>■ Neutru</span>
        {clickable && <span className="ml-auto" style={{ color: "rgba(99,102,241,0.6)" }}>🤖 click pentru analiză</span>}
      </div>
    </div>
  );
}

function ClassifyProgress({ pageId }) {
  const [status, setStatus] = useState({ done: 0, total: 0, errors: 0 });
  useEffect(() => {
    const url = pageId ? `${API}/stats/pages/${pageId}/classification-status` : `${API}/stats/classification-status`;
    const poll = setInterval(async () => { try { const res = await axios.get(url); setStatus(res.data); } catch (e) {} }, 1000);
    return () => clearInterval(poll);
  }, [pageId]);
  const pct = status.total > 0 ? Math.round((status.done / status.total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1" style={{ color: "#a5b4fc" }}><span>Se clasifică... {status.done}/{status.total}</span><span>{pct}%</span></div>
      <div className="w-full rounded-full h-1.5" style={{ background: "rgba(255,255,255,0.06)" }}><div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}></div></div>
      {status.errors > 0 && <p className="text-xs mt-1" style={{ color: "#fca5a5" }}>Erori: {status.errors}</p>}
    </div>
  );
}

function ClassifySection({ pageId, unclassified, withParent, total, onDone }) {
  const [classifying, setClassifying] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const confirmActions = {
    "unclassified": { title: "Clasifică neclasificate", message: `Clasifică ${unclassified} comentarii fără clasificare.`, color: "#a5b4fc" },
    "context": { title: "Reclasifică reply-uri cu context", message: `Reclasifică ${withParent} reply-uri cu context părinte.`, color: "#fde047" },
    "all": { title: "Reclasifică toate", message: `Reclasifică TOATE cele ${total} comentarii. Durează mult!`, color: "#fca5a5" },
  };
  const run = async (mode) => {
    setClassifying(true); setResult(null); setShowConfirm(null);
    const url = pageId ? `${API}/stats/pages/${pageId}/start-classification/${mode}` : `${API}/stats/start-classification/${mode}`;
    const statusUrl = pageId ? `${API}/stats/pages/${pageId}/classification-status` : `${API}/stats/classification-status`;
    try {
      await axios.post(url); await new Promise(r => setTimeout(r, 500));
      const poll = setInterval(async () => {
        try { const res = await axios.get(statusUrl); if (res.data.finished) { clearInterval(poll); setResult(res.data); setClassifying(false); if (onDone) onDone(); } }
        catch (e) { clearInterval(poll); setClassifying(false); }
      }, 3000);
    } catch (e) { setResult({ error: "Eroare" }); setClassifying(false); }
  };
  return (
    <div className="rounded-2xl p-5" style={card}>
      {showConfirm && confirmActions[showConfirm] && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(0,0,0,0.8)" }}>
          <div className="rounded-2xl p-6 max-w-md w-full mx-4" style={card}>
            <h3 className="text-lg font-bold mb-2 text-white">{confirmActions[showConfirm].title}</h3>
            <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.5)" }}>{confirmActions[showConfirm].message}</p>
            <div className="flex gap-3">
              <button onClick={() => run(showConfirm)} className="flex-1 py-2 rounded-xl text-sm font-medium" style={{ background: "rgba(99,102,241,0.3)", color: confirmActions[showConfirm].color, border: `1px solid ${confirmActions[showConfirm].color}` }}>Da, continuă</button>
              <button onClick={() => setShowConfirm(null)} className="flex-1 py-2 rounded-xl text-sm font-medium" style={btnGray}>Anulează</button>
            </div>
          </div>
        </div>
      )}
      <h3 className="font-semibold text-white mb-4">Gestionare Clasificări</h3>
      {result && <div className="rounded-xl p-3 mb-4 text-sm" style={result.error ? { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#fca5a5" } : { background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", color: "#86efac" }}>{result.error || `✓ Clasificate: ${result.done} | Erori: ${result.errors} | Total: ${result.total}`}</div>}
      {classifying && <div className="rounded-xl p-3 mb-4" style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)" }}><ClassifyProgress pageId={pageId} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => setShowConfirm("unclassified")} disabled={classifying || unclassified === 0} className="p-3 rounded-xl text-left disabled:opacity-40 transition-all" style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)" }}>
          <p className="text-lg font-bold" style={{ color: "#a5b4fc" }}>{unclassified}</p><p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>Clasifică neclasificate</p>
        </button>
        <button onClick={() => setShowConfirm("context")} disabled={classifying || withParent === 0} className="p-3 rounded-xl text-left disabled:opacity-40 transition-all" style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.2)" }}>
          <p className="text-lg font-bold" style={{ color: "#fde047" }}>{withParent}</p><p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>Reclasifică reply-uri</p>
        </button>
        <button onClick={() => setShowConfirm("all")} disabled={classifying} className="p-3 rounded-xl text-left disabled:opacity-40 transition-all" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.15)" }}>
          <p className="text-lg font-bold" style={{ color: "#fca5a5" }}>{total}</p><p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>Reclasifică toate</p>
        </button>
      </div>
    </div>
  );
}

function CommentersTable({ commenters, onSelectCommenter }) {
  const [filter, setFilter] = useState("all");
  const getDominant = (c) => {
    const map = { sustinator: c.sustinator, critic: c.critic, troll: c.troll, intrebare: c.intrebare, neutru: c.neutru };
    return Object.entries(map).sort((a, b) => b[1] - a[1])[0]?.[0] || "neutru";
  };
  const filtered = filter === "all" ? commenters : commenters.filter(c => {
    if (filter === "sustinator") return c.sustinator > 0 && c.troll === 0 && c.critic === 0;
    if (filter === "critic") return c.critic > 0;
    if (filter === "troll") return c.troll > 0;
    if (filter === "blocat") return c.is_blocked;
    return true;
  });
  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        {[{id:"all",label:"Toți"},{id:"sustinator",label:"Susținători"},{id:"critic",label:"Critici"},{id:"troll",label:"Troli"},{id:"blocat",label:"Blocați"}].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all" style={filter === f.id ? btnPrimary : btnGray}>{f.label}</button>
        ))}
        <span className="text-xs ml-auto self-center" style={{ color: "rgba(255,255,255,0.3)" }}>{filtered.length} utilizatori</span>
      </div>
      <div className="space-y-2">
        {filtered.map((c, i) => {
          const dominant = getDominant(c);
          return (
            <div key={c.from_id} className="rounded-xl p-3 flex items-center gap-3" style={card}>
              <span className="text-sm font-bold w-5 text-center" style={{ color: "rgba(255,255,255,0.2)" }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-white truncate cursor-pointer hover:text-indigo-300 transition-colors" onClick={() => onSelectCommenter && onSelectCommenter(c.from_id, c.from_name)}>{c.from_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={classificationStyles[dominant] || classificationStyles.neutru}>{dominant}</span>
                  {c.is_blocked === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.15)", color: "#fdba74" }}>blocat</span>}
                </div>
                <div className="flex gap-3 text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
                  {c.sustinator > 0 && <span style={{ color: "#86efac" }}>✓ {c.sustinator}</span>}
                  {c.critic > 0 && <span style={{ color: "#fde047" }}>⚠ {c.critic}</span>}
                  {c.troll > 0 && <span style={{ color: "#fca5a5" }}>✗ {c.troll}</span>}
                  {c.intrebare > 0 && <span style={{ color: "#a5b4fc" }}>? {c.intrebare}</span>}
                  <span>{new Date(c.last_seen).toLocaleDateString('ro-RO')}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0"><p className="text-lg font-bold text-white">{c.total}</p><p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>comentarii</p></div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-center py-8 text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>Niciun comentator</div>}
      </div>
    </div>
  );
}

function CommenterProfile({ fromId, fromName, onBack, onReclassified }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { const res = await axios.get(`${API}/stats/commenters/${fromId}/chain`); setData(res.data); setLoading(false); };
  useEffect(() => { load(); }, [fromId]);
  if (loading) return <div className="text-sm py-8" style={{ color: "rgba(255,255,255,0.4)" }}>Se incarca...</div>;
  if (!data) return null;
  const { profile, comments } = data;
  const dominant = Object.entries({ sustinator: profile.sustinator, critic: profile.critic, troll: profile.troll, intrebare: profile.intrebare, neutru: profile.neutru }).sort((a, b) => b[1] - a[1])[0]?.[0] || "neutru";
  const reclassify = async (commentId, cls) => { await axios.post(`${API}/comments/${commentId}/classify-manual`, { classification: cls }); await load(); if (onReclassified) onReclassified(); };
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>← Înapoi</button>
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-bold text-white">{profile.from_name}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[dominant]}>{dominant}</span>
          {profile.is_blocked === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.15)", color: "#fdba74" }}>blocat</span>}
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        {[{label:"Total",value:profile.total,color:"white"},{label:"Susținători",value:profile.sustinator,color:"#86efac"},{label:"Critici",value:profile.critic,color:"#fde047"},{label:"Troli",value:profile.troll,color:"#fca5a5"},{label:"Întrebări",value:profile.intrebare,color:"#a5b4fc"}].map((s, i) => (
          <div key={i} className="rounded-xl p-4 text-center" style={card}><p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p><p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.3)" }}>{s.label}</p></div>
        ))}
      </div>
      <div className="rounded-2xl p-5" style={card}>
        <h4 className="font-semibold text-white mb-4">Toate comentariile ({comments.length})</h4>
        <div className="space-y-4">
          {comments.map(c => (
            <div key={c.id} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  {c.classification && <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[c.classification] || classificationStyles.neutru}>{c.classification}</span>}
                  {c.is_hidden === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>ascuns</span>}
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>{new Date(c.created_time).toLocaleString("ro-RO")}</span>
                </div>
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{c.page_name}</span>
              </div>
              {c.parent_chain && c.parent_chain.length > 0 && (
                <div className="mb-3 space-y-1">
                  {c.parent_chain.map((p, i) => (
                    <div key={p.id} className="rounded-lg px-3 py-2 text-xs flex items-start gap-2" style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.08)", marginLeft: i * 8 + "px" }}>
                      <span style={{ color: "rgba(255,255,255,0.25)" }}>↳</span>
                      <div><span className="font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{p.from_name}: </span><span style={{ color: "rgba(255,255,255,0.4)" }}>{p.message}</span>{p.classification && <span className="ml-2 px-1.5 py-0.5 rounded-full" style={classificationStyles[p.classification] || classificationStyles.neutru}>{p.classification}</span>}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-lg px-3 py-2 mb-3" style={{ background: "rgba(99,102,241,0.06)", borderLeft: "2px solid rgba(99,102,241,0.3)" }}><p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>{c.message}</p></div>
              {c.post_message && <div className="rounded-lg px-3 py-1.5 text-xs mb-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}><span style={{ color: "rgba(255,255,255,0.25)" }}>Postarea: </span><span style={{ color: "rgba(255,255,255,0.4)" }}>{c.post_message?.substring(0, 80)}...</span></div>}
              <div className="flex gap-1 flex-wrap">
                {["sustinator","critic","troll","intrebare","neutru"].map(cls => (
                  <button key={cls} onClick={() => reclassify(c.id, cls)} className="text-xs px-2 py-0.5 rounded-full transition-all" style={c.classification === cls ? classificationStyles[cls] : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.08)" }}>{cls}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClassificationsChart({ classifications }) {
  const total = Object.values(classifications).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-sm" style={{ color: "rgba(255,255,255,0.3)" }}>Nu există comentarii clasificate</p>;
  return (
    <div className="space-y-3">
      {Object.entries(classifications).map(([cls, count]) => (
        <div key={cls}>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[cls] || classificationStyles.neutru}>{cls}</span>
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>{Math.round(count / total * 100)}%</span>
          </div>
          <MiniBar value={count} max={total} color={cls==='sustinator'?'#86efac':cls==='troll'?'#fca5a5':cls==='critic'?'#fde047':cls==='intrebare'?'#a5b4fc':'rgba(255,255,255,0.3)'} />
        </div>
      ))}
    </div>
  );
}

function PostDetail({ pageId, postId, onBack, backLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiModal, setAiModal] = useState(null);
  useEffect(() => { axios.get(`${API}/stats/pages/${pageId}/posts/${postId}`).then(res => { setData(res.data); setLoading(false); }); }, [pageId, postId]);
  if (loading) return <div className="text-sm py-8" style={{ color: "rgba(255,255,255,0.4)" }}>Se incarca...</div>;
  if (!data || data.error) return <div className="text-sm py-8" style={{ color: "#fca5a5" }}>Eroare la încărcare</div>;
  const { post, comments, top_commenters } = data;
  const total = post.total_comments || 0;
  const sentimentData = [
    { label: "Susținători", value: post.positive || 0, color: "#86efac" },
    { label: "Critici", value: post.negative || 0, color: "#fde047" },
    { label: "Troli", value: post.trolls || 0, color: "#fca5a5" },
    { label: "Întrebări", value: post.questions || 0, color: "#a5b4fc" },
    { label: "Neutru", value: post.neutral || 0, color: "rgba(255,255,255,0.2)" },
  ];
  const sentimentTotal = sentimentData.reduce((s, d) => s + d.value, 0);
  return (
    <div>
      {aiModal && (
        <AIAnalysisModal {...aiModal} onClose={() => setAiModal(null)}>
          {aiModal.chartType==="sentiment_pie"&&<PieChart data={sentimentData} size={150} />}
          {aiModal.chartType==="reactions_pie"&&<PieChart size={120} data={[{label:"👍 Like",value:post.like_count||0,color:"#6366f1"},{label:"❤️ Love",value:post.love_count||0,color:"#ec4899"},{label:"😂 Haha",value:post.haha_count||0,color:"#fde047"},{label:"😮 Wow",value:post.wow_count||0,color:"#f97316"},{label:"😢 Sad",value:post.sad_count||0,color:"#60a5fa"},{label:"😠 Angry",value:post.angry_count||0,color:"#fca5a5"}]} />}
        </AIAnalysisModal>
      )}
      <button onClick={onBack} className="px-3 py-1.5 rounded-xl text-xs mb-6" style={btnGray}>← {backLabel || "Înapoi la postări"}</button>
      <div className="rounded-2xl p-5 mb-4" style={{ background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.2)" }}>
        <p className="text-xs mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>{toLocal(post.created_time)}</p>
        <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.8)" }}>{post.message?.substring(0, 200)}{post.message?.length > 200 ? "..." : ""}</p>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mb-3">
          {[{label:"Reacții",value:post.reactions_total||0,color:"#fde047"},{label:"Comentarii",value:total,color:"white"},{label:"Susținători",value:post.positive||0,color:"#86efac"},{label:"Critici",value:post.negative||0,color:"#fde047"},{label:"Troli",value:post.trolls||0,color:"#fca5a5"},{label:"Întrebări",value:post.questions||0,color:"#a5b4fc"},{label:"Neutru",value:post.neutral||0,color:"rgba(255,255,255,0.4)"}].map((s, i) => (
            <div key={i} className="rounded-lg p-2 text-center" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}><p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p><p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{s.label}</p></div>
          ))}
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
          {[{emoji:"👍",value:post.like_count||0},{emoji:"❤️",value:post.love_count||0},{emoji:"😂",value:post.haha_count||0},{emoji:"😮",value:post.wow_count||0},{emoji:"😢",value:post.sad_count||0},{emoji:"😠",value:post.angry_count||0}].map((r, i) => (
            <div key={i} className="rounded-lg p-2 text-center" style={{ background: "rgba(255,255,255,0.02)" }}><p className="text-base">{r.emoji}</p><p className="text-xs font-medium text-white">{r.value}</p></div>
          ))}
        </div>
        {total > 0 && <SentimentBar positive={post.positive||0} negative={post.negative||0} trolls={post.trolls||0} total={total} />}
      </div>
      {data.avg_response && data.avg_response.total_replies > 0 && (
        <div className="rounded-2xl p-5 mb-4" style={{background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}>
          <h4 className="font-semibold text-white mb-4">⏱️ Timp Mediu Răspuns</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {label:"Timp mediu", value: data.avg_response.avg_formatted, color: data.avg_response.avg_minutes <= 60 ? "#86efac" : data.avg_response.avg_minutes <= 180 ? "#fde047" : "#fca5a5"},
              {label:"Cel mai rapid", value: data.avg_response.min_formatted, color:"#86efac"},
              {label:"Cel mai lent", value: data.avg_response.max_formatted, color:"#fca5a5"},
              {label:"Total răspunsuri", value: data.avg_response.total_replies, color:"white"},
            ].map((s,i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
                <p className="text-xl font-bold mb-1" style={{color:s.color}}>{s.value}</p>
                <p className="text-xs" style={{color:"rgba(255,255,255,0.35)"}}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="rounded-2xl p-5" style={card}>
          <h4 className="font-semibold text-white mb-3">Top Comentatori</h4>
          <div className="space-y-2">
            {top_commenters.map((c, i) => (
              <div key={c.from_id} className="flex items-center gap-2 rounded-xl p-2" style={{ background: "rgba(255,255,255,0.02)" }}>
                <span className="text-xs w-4" style={{ color: "rgba(255,255,255,0.2)" }}>{i+1}</span>
                <span className="text-xs flex-1 text-white truncate">{c.from_name}</span>
                {c.sustinator>0&&<span className="text-xs" style={{color:"#86efac"}}>✓{c.sustinator}</span>}
                {c.critic>0&&<span className="text-xs" style={{color:"#fde047"}}>⚠{c.critic}</span>}
                {c.troll>0&&<span className="text-xs" style={{color:"#fca5a5"}}>✗{c.troll}</span>}
                <span className="text-xs font-bold text-white">{c.total}</span>
              </div>
            ))}
            {top_commenters.length===0&&<p className="text-xs" style={{color:"rgba(255,255,255,0.2)"}}>Niciun comentariu</p>}
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-3">Distribuție Sentiment</h4>
            <PieChart data={sentimentData} size={100}
              onClick={() => setAiModal({ chartType:"sentiment_pie", title:"Analiză Sentiment Postare", context:`Postare publicată la ${toLocal(post.created_time)}`,
                data:{sustinator:post.positive||0,sustinator_pct:sentimentTotal>0?Math.round((post.positive||0)/sentimentTotal*100):0,critic:post.negative||0,critic_pct:sentimentTotal>0?Math.round((post.negative||0)/sentimentTotal*100):0,troll:post.trolls||0,troll_pct:sentimentTotal>0?Math.round((post.trolls||0)/sentimentTotal*100):0,intrebare:post.questions||0,intrebare_pct:sentimentTotal>0?Math.round((post.questions||0)/sentimentTotal*100):0,neutru:post.neutral||0,neutru_pct:sentimentTotal>0?Math.round((post.neutral||0)/sentimentTotal*100):0,total:sentimentTotal}
              })} />
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-3">Tipuri Reacții</h4>
            <PieChart data={[
              {label:"👍 Like",value:post.like_count||0,color:"#6366f1"},
              {label:"❤️ Love",value:post.love_count||0,color:"#ec4899"},
              {label:"😂 Haha",value:post.haha_count||0,color:"#fde047"},
              {label:"😮 Wow",value:post.wow_count||0,color:"#f97316"},
              {label:"😢 Sad",value:post.sad_count||0,color:"#60a5fa"},
              {label:"😠 Angry",value:post.angry_count||0,color:"#fca5a5"},
            ]} size={90}
            clickable={true}
            onClick={()=>{setAiModal({chartType:"reactions_pie",title:`Analiză Reacții — ${post.page_name||"postare"}`,context:`Postare: ${post.message?.substring(0,60)||"fara text"}`,data:{like_count:post.like_count||0,love_count:post.love_count||0,haha_count:post.haha_count||0,wow_count:post.wow_count||0,sad_count:post.sad_count||0,angry_count:post.angry_count||0,total:post.reactions_total||0}});}} />
            <p className="text-xs mt-1" style={{color:"rgba(99,102,241,0.6)"}}>🤖 click pentru analiză</p>
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-3">Comentarii în timp</h4>
            <StackedDailyChart data={data?.comments_over_time || []} height={110} />
          </div>
        </div>
      </div>
      <div className="rounded-2xl p-5" style={card}>
        <h4 className="font-semibold text-white mb-4">Comentarii ({comments.length})</h4>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {comments.map(c => (
            <div key={c.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.02)", borderLeft: `2px solid ${c.parent_id?"rgba(255,255,255,0.08)":"rgba(99,102,241,0.3)"}`, marginLeft: c.parent_id?"16px":"0" }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-white">{c.from_name}</span>
                {c.classification&&<span className="text-xs px-1.5 py-0.5 rounded-full" style={classificationStyles[c.classification]||classificationStyles.neutru}>{c.classification}</span>}
                <span className="text-xs ml-auto" style={{color:"rgba(255,255,255,0.25)"}}>{new Date(c.created_time).toLocaleString("ro-RO")}</span>
              </div>
              <p className="text-xs" style={{color:"rgba(255,255,255,0.6)"}}>{c.message}</p>
            </div>
          ))}
          {comments.length===0&&<p className="text-xs text-center py-4" style={{color:"rgba(255,255,255,0.2)"}}>Niciun comentariu</p>}
        </div>
      </div>
    </div>
  );
}

function PageDetail({ page, onBack, initialPostId, onBackToCampaign }) {
  const [activeTab, setActiveTab] = useState(initialPostId ? "postari" : "sumar");
  const [data, setData] = useState(null);
  const [postsData, setPostsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedCommenter, setSelectedCommenter] = useState(null);
  const [selectedPost, setSelectedPost] = useState(initialPostId || null);
  const [aiModal, setAiModal] = useState(null);
  const [avgResponse, setAvgResponse] = useState(null);
  const fetchData = async () => { 
    setLoading(true); 
    const [res, ar] = await Promise.all([
      axios.get(`${API}/stats/pages/${page.id}`),
      axios.get(`${API}/stats/pages/${page.id}/avg-response-time`)
    ]);
    setData(res.data); 
    setAvgResponse(ar.data);
    setLoading(false); 
  };
  const fetchPosts = async () => { setLoadingPosts(true); const res = await axios.get(`${API}/stats/pages/${page.id}/posts`); setPostsData(res.data); setLoadingPosts(false); };
  useEffect(() => { fetchData(); }, [page.id]);
  useEffect(() => { if (activeTab==="postari"&&!postsData) fetchPosts(); }, [activeTab]);
  if (loading) return <div className="text-sm py-8" style={{color:"rgba(255,255,255,0.4)"}}>Se incarca...</div>;
  if (selectedCommenter) return <CommenterProfile fromId={selectedCommenter.id} fromName={selectedCommenter.name} onBack={()=>setSelectedCommenter(null)} onReclassified={fetchData} />;
  if (selectedPost) return <PostDetail pageId={page.id} postId={selectedPost} onBack={()=>{ if(initialPostId && onBackToCampaign) { onBackToCampaign(); } else { setSelectedPost(null); } }} backLabel={initialPostId ? "Înapoi la campanie" : "Înapoi la postări"} />;
  const clsTotal = Object.values(data.classifications).reduce((a,b)=>a+b,0);
  const tabs = [{id:"sumar",label:"📊 Sumar"},{id:"postari",label:"📝 Postări"},{id:"comentatori",label:"👥 Comentatori"},{id:"clasificare",label:"⚙️ Clasificare"}];
  return (
    <div>
      {aiModal && (
        <AIAnalysisModal {...aiModal} onClose={() => setAiModal(null)}>
          {aiModal.chartType==="sentiment_pie"&&<PieChart size={150} data={[{label:"Susținători",value:data.classifications.sustinator||0,color:"#86efac"},{label:"Critici",value:data.classifications.critic||0,color:"#fde047"},{label:"Troli",value:data.classifications.troll||0,color:"#fca5a5"},{label:"Întrebări",value:data.classifications.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:data.classifications.neutru||0,color:"rgba(255,255,255,0.15)"}]} />}
          {aiModal.chartType==="timeline"&&<StackedDailyChart data={data.comments_over_time} height={130} />}
          {aiModal.chartType==="reactions_pie"&&aiModal.chartData&&<PieChart size={120} data={[{label:"👍 Like",value:aiModal.chartData.like_count||0,color:"#6366f1"},{label:"❤️ Love",value:aiModal.chartData.love_count||0,color:"#ec4899"},{label:"😂 Haha",value:aiModal.chartData.haha_count||0,color:"#fde047"},{label:"😮 Wow",value:aiModal.chartData.wow_count||0,color:"#f97316"},{label:"😢 Sad",value:aiModal.chartData.sad_count||0,color:"#60a5fa"},{label:"😠 Angry",value:aiModal.chartData.angry_count||0,color:"#fca5a5"}]} />}
        </AIAnalysisModal>
      )}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>← Înapoi</button>
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-bold text-white">{data.page.name}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full" style={pageTypeStyles[data.page.page_type]}>{data.page.page_type}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Postări" value={data.engagement.post_count} color="white" />
        <StatCard label="Comentarii" value={data.total_comments} color="#a5b4fc" />
        <StatCard label="Reacții" value={data.engagement.total_reactions} color="#86efac" />
        <StatCard label="Neclasificate" value={data.unclassified} color="#fde047" />
      </div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(t => <button key={t.id} onClick={()=>setActiveTab(t.id)} className="px-4 py-2 rounded-xl text-sm font-medium transition-all" style={activeTab===t.id?btnPrimary:btnGray}>{t.label}</button>)}
      </div>
      {activeTab==="sumar"&&(
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl p-5" style={card}>
              <h4 className="font-semibold text-white mb-4">Distribuție Sentiment</h4>
              <PieChart data={[{label:"Susținători",value:data.classifications.sustinator||0,color:"#86efac"},{label:"Critici",value:data.classifications.critic||0,color:"#fde047"},{label:"Troli",value:data.classifications.troll||0,color:"#fca5a5"},{label:"Întrebări",value:data.classifications.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:data.classifications.neutru||0,color:"rgba(255,255,255,0.15)"}]} size={120}
                onClick={()=>setAiModal({chartType:"sentiment_pie",title:`Analiză Sentiment — ${data.page.name}`,context:`Pagina ${data.page.name} (${data.page.page_type}), ${data.total_comments} comentarii totale`,data:{sustinator:data.classifications.sustinator||0,sustinator_pct:clsTotal>0?Math.round((data.classifications.sustinator||0)/clsTotal*100):0,critic:data.classifications.critic||0,critic_pct:clsTotal>0?Math.round((data.classifications.critic||0)/clsTotal*100):0,troll:data.classifications.troll||0,troll_pct:clsTotal>0?Math.round((data.classifications.troll||0)/clsTotal*100):0,intrebare:data.classifications.intrebare||0,intrebare_pct:clsTotal>0?Math.round((data.classifications.intrebare||0)/clsTotal*100):0,neutru:data.classifications.neutru||0,neutru_pct:clsTotal>0?Math.round((data.classifications.neutru||0)/clsTotal*100):0,total:clsTotal}})} />
            </div>
            <div className="rounded-2xl p-5" style={card}>
              <h4 className="font-semibold text-white mb-4">Comentarii — ultimele 30 zile</h4>
              <StackedDailyChart data={data.comments_over_time} height={130}
                onClick={()=>{const tlData=data.comments_over_time;const totalAll=tlData.reduce((s,d)=>s+(d.total||0),0);const peak=tlData.reduce((m,d)=>(d.total||0)>(m.total||0)?d:m,{total:0});const activeDays=tlData.filter(d=>(d.total||0)>0).length;setAiModal({chartType:"timeline",title:`Analiză Timeline — ${data.page.name}`,context:`Pagina ${data.page.name}, ultimele 30 zile`,data:{total:totalAll,peak_day:peak.day,peak_total:peak.total||0,avg_daily:activeDays>0?Math.round(totalAll/activeDays):0,total_positive:tlData.reduce((s,d)=>s+(d.positive||0),0),total_negative:tlData.reduce((s,d)=>s+(d.negative||0),0),total_trolls:tlData.reduce((s,d)=>s+(d.trolls||0),0),active_days:activeDays,total_days:tlData.length,days:tlData.map(d=>d.day)}});}} />
            </div>
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-4">Clasificări detaliate</h4>
            <ClassificationsChart classifications={data.classifications} />
          </div>
          {avgResponse && avgResponse.total_replies > 0 && (
            <div className="rounded-2xl p-5" style={{background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}>
              <h4 className="font-semibold text-white mb-4">⏱️ Timp Mediu Răspuns</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  {label:"Timp mediu", value: avgResponse.avg_formatted, color: avgResponse.avg_minutes <= 60 ? "#86efac" : avgResponse.avg_minutes <= 180 ? "#fde047" : "#fca5a5"},
                  {label:"Cel mai rapid", value: avgResponse.min_formatted, color:"#86efac"},
                  {label:"Cel mai lent", value: avgResponse.max_formatted, color:"#fca5a5"},
                  {label:"Total răspunsuri", value: avgResponse.total_replies, color:"white"},
                ].map((s,i) => (
                  <div key={i} className="rounded-xl p-3 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
                    <p className="text-xl font-bold mb-1" style={{color:s.color}}>{s.value}</p>
                    <p className="text-xs" style={{color:"rgba(255,255,255,0.35)"}}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab==="postari"&&(
        <div>
          {loadingPosts&&<div className="text-sm py-8" style={{color:"rgba(255,255,255,0.4)"}}>Se incarca postările...</div>}
          {postsData&&(
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl p-5" style={card}>
                  <h4 className="font-semibold text-white mb-4">Reacții per lună</h4>
                  <BarChart data={postsData.reactions_over_time.map(d=>({label:d.month,value:d.total_reactions}))} color="#6366f1" height={120} />
                </div>
                <div className="rounded-2xl p-5" style={card}>
                  <h4 className="font-semibold text-white mb-4">Distribuție Reacții</h4>
                  <PieChart data={[{label:"👍 Like",value:postsData.reaction_types.like_count||0,color:"#6366f1"},{label:"❤️ Love",value:postsData.reaction_types.love_count||0,color:"#ec4899"},{label:"😂 Haha",value:postsData.reaction_types.haha_count||0,color:"#fde047"},{label:"😮 Wow",value:postsData.reaction_types.wow_count||0,color:"#f97316"},{label:"😢 Sad",value:postsData.reaction_types.sad_count||0,color:"#60a5fa"},{label:"😠 Angry",value:postsData.reaction_types.angry_count||0,color:"#fca5a5"}]} size={100}
                    onClick={()=>{const rt=postsData.reaction_types;const rtTotal=Object.values(rt).reduce((a,b)=>a+b,0);setAiModal({chartType:"reactions_pie",title:`Analiză Reacții — ${data.page.name}`,context:`Pagina ${data.page.name}, toate postările`,data:{...rt,total:rtTotal},chartData:{...rt}});}} />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-white">Toate postările ({postsData.posts.length})</h4>
                  <button onClick={fetchPosts} className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>🔄</button>
                </div>
                {postsData.posts.map(post=>{
                  const total=post.total_comments||0;
                  return(
                    <div key={post.id} className="rounded-2xl p-4 cursor-pointer transition-all" style={card}
                      onClick={()=>setSelectedPost(post.id)}
                      onMouseEnter={e=>e.currentTarget.style.border="1px solid rgba(99,102,241,0.4)"}
                      onMouseLeave={e=>e.currentTarget.style.border="1px solid rgba(255,255,255,0.08)"}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0 mr-4">
                          <p className="text-xs mb-1" style={{color:"rgba(255,255,255,0.3)"}}>
                            {toLocal(post.created_time)}
                            {post.campaign_title&&<span className="ml-2 px-1.5 py-0.5 rounded-full text-xs" style={{background:"rgba(99,102,241,0.15)",color:"#a5b4fc"}}>📢 {post.campaign_title}</span>}
                          </p>
                          <p className="text-sm truncate" style={{color:"rgba(255,255,255,0.7)"}}>{post.message?.substring(0,100)||"— fără text —"}</p>
                        </div>
                        <span style={{color:"rgba(255,255,255,0.3)"}}>→</span>
                      </div>
                      <div className="grid grid-cols-4 md:grid-cols-7 gap-1 mb-2">
                        {[{label:"Reacții",value:post.reactions_total||0,color:"#fde047"},{label:"Comentarii",value:total,color:"white"},{label:"Susținători",value:post.positive||0,color:"#86efac"},{label:"Critici",value:post.negative||0,color:"#fde047"},{label:"Troli",value:post.trolls||0,color:"#fca5a5"}].map((s,i)=>(
                          <div key={i} className="rounded-lg p-1.5 text-center" style={{background:"rgba(255,255,255,0.02)"}}><p className="text-sm font-bold" style={{color:s.color}}>{s.value}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.3)",fontSize:9}}>{s.label}</p></div>
                        ))}
                      </div>
                      {total>0&&<SentimentBar positive={post.positive||0} negative={post.negative||0} trolls={post.trolls||0} total={total} />}
                    </div>
                  );
                })}
                {postsData.posts.length===0&&<div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.2)"}}>Nicio postare găsită</div>}
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab==="comentatori"&&<div className="rounded-2xl p-5" style={card}><h4 className="font-semibold text-white mb-4">Top Comentatori</h4><CommentersTable commenters={data.commenters} onSelectCommenter={(id,name)=>setSelectedCommenter({id,name})} /></div>}
      {activeTab==="clasificare"&&<ClassifySection pageId={page.id} unclassified={data.unclassified} withParent={data.with_parent} total={data.total_comments} onDone={fetchData} />}
    </div>
  );
}

function CampaignDetail({ campaignId, campaignTitle }) {
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [aiModal, setAiModal] = useState(null);
  const [selectedCampaignPage, setSelectedCampaignPage] = useState(null); // { pageId, postId, pageName }
  const [avgResponse, setAvgResponse] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats/campaigns/${campaignId}`).then(res=>setData(res.data));
    axios.get(`${API}/stats/campaigns/${campaignId}/charts`).then(res=>setCharts(res.data));
    axios.get(`${API}/stats/campaigns/${campaignId}/avg-response-time`).then(res=>setAvgResponse(res.data)).catch(()=>{});
  }, [campaignId]);

  if (!data) return <div className="text-center py-8 text-sm" style={{color:"rgba(255,255,255,0.3)"}}>Se incarca...</div>;

  // Drill-down pagina din campanie
  if (selectedCampaignPage) {
    return (
      <PageDetail
        page={{ id: selectedCampaignPage.pageId, name: selectedCampaignPage.pageName, page_type: selectedCampaignPage.pageType }}
        onBack={() => setSelectedCampaignPage(null)}
        initialPostId={selectedCampaignPage.postId}
        onBackToCampaign={() => setSelectedCampaignPage(null)}
      />
    );
  }

  const allPositive=(data.main_post?.positive||0)+data.pages.reduce((s,p)=>s+(p.positive||0),0);
  const allComments=(data.main_post?.total_comments||0)+data.pages.reduce((s,p)=>s+(p.total_comments||0),0);
  const allReactions=(data.main_post?.reactions_total||0)+data.pages.reduce((s,p)=>s+(p.reactions_total||0),0);
  const sentimentScore=allComments>0?Math.round((allPositive/allComments)*100):0;
  const sentimentColor=allComments===0?"rgba(255,255,255,0.3)":sentimentScore>=60?"#86efac":sentimentScore>=40?"#fde047":"#fca5a5";

  const PageStats=({item})=>{
    const total=item.total_comments||0;const pos=item.positive||0;const neg=item.negative||0;
    return(
      <div>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-1 mb-2">
          {[{label:"Reacții",value:item.reactions_total||0,color:"#fde047"},{label:"Comentarii",value:total,color:"white"},{label:"Susținători",value:pos,color:"#86efac"},{label:"Critici",value:neg,color:"#fca5a5"},{label:"Troli",value:item.trolls||0,color:"#fb923c"},{label:"Întrebări",value:item.questions||0,color:"#a5b4fc"},{label:"Neutru",value:item.neutral||0,color:"rgba(255,255,255,0.4)"}].map((s,i)=>(
            <div key={i} className="rounded-lg p-2 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",minWidth:0}}><p className="text-base font-bold" style={{color:s.color}}>{s.value}</p><p style={{color:"rgba(255,255,255,0.3)",fontSize:9}}>{s.label}</p></div>
          ))}
        </div>
        {total>0&&<SentimentBar positive={pos} negative={neg} trolls={item.trolls||0} total={total} />}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {aiModal&&(
        <AIAnalysisModal {...aiModal} onClose={()=>setAiModal(null)}>
          {aiModal.chartType==="sentiment_pie"&&charts&&<PieChart size={150} data={[{label:"Susținători",value:charts.sentiment.sustinator||0,color:"#86efac"},{label:"Critici",value:charts.sentiment.critic||0,color:"#fde047"},{label:"Troli",value:charts.sentiment.troll||0,color:"#fca5a5"},{label:"Întrebări",value:charts.sentiment.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:charts.sentiment.neutru||0,color:"rgba(255,255,255,0.15)"}]} />}
          {aiModal.chartType==="reactions_pie"&&charts&&<PieChart size={120} data={[{label:"👍 Like",value:charts.reaction_types.like_count||0,color:"#6366f1"},{label:"❤️ Love",value:charts.reaction_types.love_count||0,color:"#ec4899"},{label:"😂 Haha",value:charts.reaction_types.haha_count||0,color:"#fde047"},{label:"😮 Wow",value:charts.reaction_types.wow_count||0,color:"#f97316"},{label:"😢 Sad",value:charts.reaction_types.sad_count||0,color:"#60a5fa"},{label:"😠 Angry",value:charts.reaction_types.angry_count||0,color:"#fca5a5"}]} />}
          {aiModal.chartType==="reactions_bar"&&charts&&<BarChart data={charts.pages_chart.map(p=>({label:p.page_name.split(" ")[0],value:p.reactions_total||0,color:pageTypeColors[p.page_type]||"#6366f1"}))} height={130} />}
          {aiModal.chartType==="timeline"&&charts&&<StackedDailyChart data={charts.timeline} height={130} />}
        </AIAnalysisModal>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[{label:"Total reacții",value:allReactions,color:"#fde047"},{label:"Total comentarii",value:allComments,color:"white"},{label:"Sentiment pozitiv",value:`${sentimentScore}%`,color:sentimentColor},{label:"Susținători",value:allPositive,color:"#86efac"}].map((s,i)=>(
          <div key={i} className="rounded-xl p-3 text-center" style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)"}}><p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.35)"}}>{s.label}</p></div>
        ))}
      </div>
      {avgResponse && avgResponse.total_replies > 0 && (
        <div className="rounded-2xl p-5" style={{background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}>
          <h4 className="font-semibold text-white mb-4">⏱️ Timp Mediu Răspuns Campanie</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {label:"Timp mediu", value: avgResponse.avg_formatted, color: avgResponse.avg_minutes <= 60 ? "#86efac" : avgResponse.avg_minutes <= 180 ? "#fde047" : "#fca5a5"},
              {label:"Cel mai rapid", value: avgResponse.min_formatted, color:"#86efac"},
              {label:"Cel mai lent", value: avgResponse.max_formatted, color:"#fca5a5"},
              {label:"Total răspunsuri", value: avgResponse.total_replies, color:"white"},
            ].map((s,i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
                <p className="text-xl font-bold mb-1" style={{color:s.color}}>{s.value}</p>
                <p className="text-xs" style={{color:"rgba(255,255,255,0.35)"}}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {charts&&(
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-4">Distribuție Sentiment</h4>
            <PieChart data={[{label:"Susținători",value:charts.sentiment.sustinator||0,color:"#86efac"},{label:"Critici",value:charts.sentiment.critic||0,color:"#fde047"},{label:"Troli",value:charts.sentiment.troll||0,color:"#fca5a5"},{label:"Întrebări",value:charts.sentiment.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:charts.sentiment.neutru||0,color:"rgba(255,255,255,0.15)"}]} size={120}
              onClick={()=>{const s=charts.sentiment;const t=s.total||1;setAiModal({chartType:"sentiment_pie",title:`Analiză Sentiment — ${campaignTitle}`,context:`Campania "${campaignTitle}", ${allComments} comentarii totale`,data:{sustinator:s.sustinator||0,sustinator_pct:Math.round((s.sustinator||0)/t*100),critic:s.critic||0,critic_pct:Math.round((s.critic||0)/t*100),troll:s.troll||0,troll_pct:Math.round((s.troll||0)/t*100),intrebare:s.intrebare||0,intrebare_pct:Math.round((s.intrebare||0)/t*100),neutru:s.neutru||0,neutru_pct:Math.round((s.neutru||0)/t*100),total:t}});}} />
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-4">Tipuri Reacții</h4>
            <PieChart data={[{label:"👍 Like",value:charts.reaction_types.like_count||0,color:"#6366f1"},{label:"❤️ Love",value:charts.reaction_types.love_count||0,color:"#ec4899"},{label:"😂 Haha",value:charts.reaction_types.haha_count||0,color:"#fde047"},{label:"😮 Wow",value:charts.reaction_types.wow_count||0,color:"#f97316"},{label:"😢 Sad",value:charts.reaction_types.sad_count||0,color:"#60a5fa"},{label:"😠 Angry",value:charts.reaction_types.angry_count||0,color:"#fca5a5"}]} size={100}
              onClick={()=>{const rt=charts.reaction_types;const rtTotal=Object.values(rt).reduce((a,b)=>a+b,0);setAiModal({chartType:"reactions_pie",title:`Analiză Reacții — ${campaignTitle}`,context:`Campania "${campaignTitle}", toate paginile`,data:{...rt,total:rtTotal}});}} />
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-4">Reacții per Pagină</h4>
            <BarChart data={charts.pages_chart.map(p=>({label:p.page_name.split(" ")[0],value:p.reactions_total||0,color:pageTypeColors[p.page_type]||"#6366f1",type:p.page_type}))} height={130}
              onClick={()=>setAiModal({chartType:"reactions_bar",title:`Performanță per Pagină — ${campaignTitle}`,context:`Campania "${campaignTitle}"`,data:{pages:charts.pages_chart.map(p=>({label:p.page_name,value:p.reactions_total||0,type:p.page_type}))}})} />
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h4 className="font-semibold text-white mb-4">Comentarii în timp</h4>
            <StackedDailyChart data={charts.timeline} height={130}
              onClick={()=>{const tlData=charts.timeline;const totalAll=tlData.reduce((s,d)=>s+(d.total||0),0);const peak=tlData.reduce((m,d)=>(d.total||0)>(m.total||0)?d:m,{total:0});const activeDays=tlData.filter(d=>(d.total||0)>0).length;setAiModal({chartType:"timeline",title:`Analiză Timeline — ${campaignTitle}`,context:`Campania "${campaignTitle}"`,data:{total:totalAll,peak_day:peak.day,peak_total:peak.total||0,avg_daily:activeDays>0?Math.round(totalAll/activeDays):0,total_positive:tlData.reduce((s,d)=>s+(d.positive||0),0),total_negative:tlData.reduce((s,d)=>s+(d.negative||0),0),total_trolls:tlData.reduce((s,d)=>s+(d.trolls||0),0),active_days:activeDays,total_days:tlData.length,days:tlData.map(d=>d.day)}});}} />
          </div>
        </div>
      )}

      {/* Postarea principala — clickabila */}
      {data.main_post&&(
        <div className="rounded-2xl p-5 cursor-pointer transition-all" style={{background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}
          onClick={() => data.main_post.id && setSelectedCampaignPage({ pageId: data.main_post.page_id, postId: data.main_post.id, pageName: data.main_post.page_name, pageType: "official" })}
          onMouseEnter={e=>e.currentTarget.style.border="1px solid rgba(99,102,241,0.5)"}
          onMouseLeave={e=>e.currentTarget.style.border="1px solid rgba(99,102,241,0.2)"}>
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span>📢</span><span className="font-medium text-sm text-white">{data.main_post.page_name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(99,102,241,0.15)",color:"#a5b4fc"}}>official</span>
              {data.main_post.created_time&&<span className="text-xs" style={{color:"rgba(255,255,255,0.25)"}}>· {toLocal(data.main_post.created_time)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(34,197,94,0.15)",color:"#86efac"}}>posted</span>
              <span className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>→</span>
            </div>
          </div>
          <PageStats item={data.main_post} />
        </div>
      )}

      <h4 className="text-sm font-semibold text-white">Performanță per pagină</h4>
      {data.pages.map(page=>(
        <div key={page.page_id} className="rounded-xl p-4 transition-all"
          style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${pageTypeColors[page.page_type]||"rgba(255,255,255,0.06)"}30`,cursor:page.status==='posted'&&page.fb_post_id?"pointer":"default"}}
          onClick={() => page.status==='posted' && page.fb_post_id && setSelectedCampaignPage({ pageId: page.page_id, postId: page.fb_post_id, pageName: page.page_name, pageType: page.page_type })}
          onMouseEnter={e=>{ if(page.status==='posted'&&page.fb_post_id) e.currentTarget.style.border=`1px solid ${pageTypeColors[page.page_type]||"rgba(99,102,241,0.4)"}80`; }}
          onMouseLeave={e=>e.currentTarget.style.border=`1px solid ${pageTypeColors[page.page_type]||"rgba(255,255,255,0.06)"}30`}>
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <span>{pageTypeIcons[page.page_type]||"📄"}</span>
              <span className="font-medium text-sm text-white">{page.page_name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(255,255,255,0.06)",color:pageTypeColors[page.page_type]}}>{page.page_type}</span>
              {page.posted_at&&<span className="text-xs" style={{color:"rgba(255,255,255,0.25)"}}>· {toLocal(page.posted_at)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full" style={page.status==='posted'?{background:"rgba(34,197,94,0.15)",color:"#86efac"}:page.status==='approved'?{background:"rgba(99,102,241,0.15)",color:"#a5b4fc"}:{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.4)"}}>{page.status}</span>
              {page.status==='posted'&&page.fb_post_id&&<span className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>→</span>}
            </div>
          </div>
          {page.status==='posted'?<PageStats item={page}/>:<p className="text-xs italic" style={{color:"rgba(255,255,255,0.25)"}}>Nepublicată încă</p>}
        </div>
      ))}
    </div>
  );
}

function HoursDisplay({ hours }) {
  const [expandedHour, setExpandedHour] = useState(null);
  return (
    <div>
      <p className="text-xs font-semibold mb-2" style={{color:"rgba(255,255,255,0.3)"}}>ORE DE ACTIVITATE:</p>
      <div className="flex gap-1 flex-wrap">
        {hours.map((h, j) => {
          const negative = h.trolls + h.critics;
          const intensity = negative / Math.max(h.total, 1);
          const isExpanded = expandedHour === h.hour;
          return (
            <div key={j} className="flex flex-col">
              <button
                onClick={() => setExpandedHour(isExpanded ? null : h.hour)}
                className="rounded-lg px-2 py-1.5 text-center transition-all"
                style={{
                  minWidth:"52px",
                  background: isExpanded ? "rgba(99,102,241,0.3)" : intensity > 0.6 ? "rgba(239,68,68,0.2)" : intensity > 0.3 ? "rgba(234,179,8,0.15)" : "rgba(34,197,94,0.1)",
                  border: `1px solid ${isExpanded ? "rgba(99,102,241,0.6)" : intensity > 0.6 ? "rgba(239,68,68,0.4)" : intensity > 0.3 ? "rgba(234,179,8,0.3)" : "rgba(34,197,94,0.2)"}`
                }}>
                <p className="text-xs font-bold text-white">{h.hour}:00</p>
                <p className="text-xs" style={{color:"rgba(255,255,255,0.5)"}}>{h.total}</p>
                {negative > 0 && <p className="text-xs" style={{color:"#fca5a5"}}>🚫{negative}</p>}
              </button>
            </div>
          );
        })}
      </div>
      {expandedHour && (() => {
        const hourData = hours.find(h => h.hour === expandedHour);
        if (!hourData?.quarters?.length) return null;
        return (
          <div className="mt-2 p-3 rounded-xl" style={{background:"rgba(99,102,241,0.06)",border:"1px solid rgba(99,102,241,0.2)"}}>
            <p className="text-xs font-semibold mb-2" style={{color:"#a5b4fc"}}>🕐 Detaliu {expandedHour}</p>
            <div className="flex gap-2 flex-wrap">
              {hourData.quarters.map((q, k) => {
                const negative = q.trolls + q.critics;
                const intensity = negative / Math.max(q.total, 1);
                return (
                  <div key={k} className="rounded-lg px-3 py-2 text-center"
                    style={{
                      minWidth:"70px",
                      background: intensity > 0.6 ? "rgba(239,68,68,0.2)" : intensity > 0.3 ? "rgba(234,179,8,0.15)" : "rgba(34,197,94,0.1)",
                      border: `1px solid ${intensity > 0.6 ? "rgba(239,68,68,0.4)" : intensity > 0.3 ? "rgba(234,179,8,0.3)" : "rgba(34,197,94,0.2)"}`
                    }}>
                    <p className="text-xs font-bold text-white">{q.label}</p>
                    <p className="text-sm font-bold text-white">{q.total}</p>
                    {negative > 0 && <p className="text-xs" style={{color:"#fca5a5"}}>🚫 {negative}</p>}
                    <p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{q.unique_users} useri</p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function Statistics() {
  const [activeSection, setActiveSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCommenter, setSelectedCommenter] = useState(null);
  const [allCommenters, setAllCommenters] = useState([]);
  const [aiModal, setAiModal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [avgResponse, setAvgResponse] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats/accounts`).then(res => setAccounts(res.data.accounts));
  }, []);

  useEffect(() => { fetchAll(); }, [selectedAccount]);

  const fetchAll = async () => {
    setLoading(true);
    const params = selectedAccount ? `?account_id=${selectedAccount}` : "";
    try {
      // allSettled: daca un endpoint pica, celelalte se afiseaza totusi
      const [ov, pg, cm, ca, cn, ar] = await Promise.allSettled([
        axios.get(`${API}/stats/overview${params}`),
        axios.get(`${API}/stats/pages${params}`),
        axios.get(`${API}/stats/commenters${params}`),
        axios.get(`${API}/stats/campaigns${params}`),
        axios.get(`${API}/stats/classification-counts${params}`),
        axios.get(`${API}/stats/avg-response-time${params}`),
      ]);
      const ok = r => r.status === "fulfilled" ? r.value.data : null;
      const esuate = [ov, pg, cm, ca, cn, ar].filter(r => r.status === "rejected");
      if (esuate.length) console.warn(`Statistici: ${esuate.length} cereri esuate`, esuate.map(r => r.reason?.message));

      if (ok(ov)) setOverview(ok(ov));
      setPages(ok(pg)?.pages || []);
      setAllCommenters(ok(cm)?.commenters || []);
      setCampaigns(ok(ca)?.campaigns || []);
      if (ok(cn)) setCounts(ok(cn));
      if (ok(ar)) setAvgResponse(ok(ar));
    } catch (e) {
      console.error("Eroare la incarcarea statisticilor:", e);
    } finally {
      setLoading(false);
    }
  };

  const [coordData, setCoordData] = useState(null);
  const [coordLoading, setCoordLoading] = useState(false);
  const [coordTab, setCoordTab] = useState("supporters");
  const [summarySubTab, setSummarySubTab] = useState("negativi");
  const [analyzingThemes, setAnalyzingThemes] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(null);
  const [counterNarrative, setCounterNarrative] = useState(null);
  const [generatingCounter, setGeneratingCounter] = useState(null);
  const [themesData, setThemesData] = useState(null);
  const [themesLoading, setThemesLoading] = useState(false);

  const fetchThemes = async () => {
    setThemesLoading(true);
    try {
      const res = await axios.get(`${API}/themes/results`);
      setThemesData(res.data);
    } catch(e) { console.error(e); }
    setThemesLoading(false);
  };

  useEffect(() => {
    if (activeSection === "teme" && !themesData) fetchThemes();
  }, [activeSection]);

  const fetchCoordination = async () => {
    setCoordLoading(true);
    try {
      const params = selectedAccount ? `?account_id=${selectedAccount}` : "";
      const [summary, bursts, supporters, postsAttack] = await Promise.all([
        axios.get(`${API}/coordination/summary${params}`),
        axios.get(`${API}/coordination/bursts${params}&min_users=3`),
        axios.get(`${API}/coordination/supporters${params}`),
        axios.get(`${API}/coordination/posts-attack${params}`),
      ]);
      setCoordData({
        summary: summary.data,
        bursts: bursts.data,
        supporters: supporters.data,
        postsAttack: postsAttack.data,
      });
    } catch (e) { console.error(e); }
    setCoordLoading(false);
  };

  useEffect(() => {
    if (activeSection === "coordonare") { setCoordData(null); fetchCoordination(); }
  }, [activeSection, selectedAccount]);

  const sections = [
    { id: "overview", label: "📊 Overview" },
    { id: "pages", label: "📄 Pagini" },
    { id: "campanii", label: "📢 Campanii" },

  ];

  const maxReactions = pages.length > 0 ? Math.max(...pages.map(p => p.total_reactions)) : 0;
  const selectedAccountName = selectedAccount ? accounts.find(a => a.id === selectedAccount)?.name : null;
  if (loading) return <div className="text-sm py-8" style={{ color: "rgba(255,255,255,0.4)" }}>Se incarca...</div>;

  return (
    <div>
      {aiModal && (
        <AIAnalysisModal {...aiModal} onClose={() => setAiModal(null)}>
          {aiModal.chartType==="sentiment_pie"&&overview&&<PieChart size={150} data={[{label:"Susținători",value:overview.classifications.sustinator||0,color:"#86efac"},{label:"Critici",value:overview.classifications.critic||0,color:"#fde047"},{label:"Troli",value:overview.classifications.troll||0,color:"#fca5a5"},{label:"Întrebări",value:overview.classifications.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:overview.classifications.neutru||0,color:"rgba(255,255,255,0.15)"}]} />}
          {aiModal.chartType==="timeline"&&overview&&<StackedDailyChart data={overview.comments_over_time} height={130} />}
        </AIAnalysisModal>
      )}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Statistici</h2>
        <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>Analytics și insights</p>
      </div>
      {selectedCommenter && <CommenterProfile fromId={selectedCommenter.id} fromName={selectedCommenter.name} onBack={() => setSelectedCommenter(null)} />}
      {!selectedPage && !selectedCampaign && !selectedCommenter && (
        <div className="flex gap-2 mb-6 flex-wrap items-center">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} className="px-4 py-2 rounded-xl text-sm font-medium transition-all" style={activeSection === s.id ? btnPrimary : btnGray}>{s.label}</button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <div>
              <p className="text-xs mb-1 font-medium text-right" style={{color:"rgba(255,255,255,0.6)"}}>Conturi</p>
              <div className="flex rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
                <button onClick={() => setSelectedAccount(null)} className="px-3 py-2 text-xs font-medium transition-all" style={!selectedAccount ? btnPrimary : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.5)" }}>🌐 Toate</button>
                {accounts.map(acc => (
                  <button key={acc.id} onClick={() => setSelectedAccount(acc.id)} className="px-3 py-2 text-xs font-medium transition-all border-l"
                    style={selectedAccount === acc.id ? btnPrimary : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.08)" }}>
                    {acc.name}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={fetchAll} className="px-3 py-2 rounded-xl text-sm" style={btnGray} title="Reîncarcă datele din baza de date">🔄</button>
          </div>
        </div>
      )}
      {selectedAccountName && !selectedPage && !selectedCampaign && !selectedCommenter && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(99,102,241,0.15)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.3)" }}>👤 {selectedAccountName}</span>
        </div>
      )}

      {activeSection === "overview" && !selectedPage && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Postări" value={overview.total_posts} />
            <StatCard label="Total Comentarii" value={overview.total_comments} color="#a5b4fc" />
            <StatCard label="Total Reacții" value={overview.total_reactions} color="#86efac" />
            <StatCard label="Campanii" value={overview.total_campaigns} color="#fde047" />
          </div>
          {avgResponse && (
            <div className="rounded-2xl p-5" style={{background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}>
              <h3 className="font-semibold text-white mb-4">⏱️ Timp Mediu Răspuns — ultimele 30 zile</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {label:"Timp mediu", value: avgResponse.avg_formatted, color: avgResponse.avg_minutes <= 60 ? "#86efac" : avgResponse.avg_minutes <= 180 ? "#fde047" : "#fca5a5"},
                  {label:"Cel mai rapid", value: avgResponse.min_formatted, color:"#86efac"},
                  {label:"Cel mai lent", value: avgResponse.max_formatted, color:"#fca5a5"},
                  {label:"Total răspunsuri", value: avgResponse.total_replies, color:"white"},
                ].map((s,i) => (
                  <div key={i} className="rounded-xl p-4 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
                    <p className="text-2xl font-bold mb-1" style={{color:s.color}}>{s.value}</p>
                    <p className="text-xs" style={{color:"rgba(255,255,255,0.35)"}}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {counts && <ClassifySection pageId={null} unclassified={counts.unclassified} withParent={counts.with_parent} total={counts.total} onDone={fetchAll} />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl p-5" style={card}>
              <h3 className="font-semibold text-white mb-4">Distribuție Clasificări Globală</h3>
              <PieChart data={[{label:"Susținători",value:overview.classifications.sustinator||0,color:"#86efac"},{label:"Critici",value:overview.classifications.critic||0,color:"#fde047"},{label:"Troli",value:overview.classifications.troll||0,color:"#fca5a5"},{label:"Întrebări",value:overview.classifications.intrebare||0,color:"#a5b4fc"},{label:"Neutru",value:overview.classifications.neutru||0,color:"rgba(255,255,255,0.15)"}]} size={120}
                onClick={()=>{const cls=overview.classifications;const t=Object.values(cls).reduce((a,b)=>a+b,0)||1;setAiModal({chartType:"sentiment_pie",title:`Analiză Sentiment${selectedAccountName?" — "+selectedAccountName:" Global"}`,context:`${overview.total_comments} comentarii${selectedAccountName?" pentru "+selectedAccountName:""}`,data:{sustinator:cls.sustinator||0,sustinator_pct:Math.round((cls.sustinator||0)/t*100),critic:cls.critic||0,critic_pct:Math.round((cls.critic||0)/t*100),troll:cls.troll||0,troll_pct:Math.round((cls.troll||0)/t*100),intrebare:cls.intrebare||0,intrebare_pct:Math.round((cls.intrebare||0)/t*100),neutru:cls.neutru||0,neutru_pct:Math.round((cls.neutru||0)/t*100),total:t}});}} />
            </div>
            <div className="rounded-2xl p-5" style={card}>
              <h3 className="font-semibold text-white mb-4">Comentarii — ultimele 30 zile</h3>
              <StackedDailyChart data={overview.comments_over_time} height={130}
                onClick={()=>{const tlData=overview.comments_over_time;const totalAll=tlData.reduce((s,d)=>s+(d.total||0),0);const peak=tlData.reduce((m,d)=>(d.total||0)>(m.total||0)?d:m,{total:0});const activeDays=tlData.filter(d=>(d.total||0)>0).length;setAiModal({chartType:"timeline",title:`Analiză Timeline${selectedAccountName?" — "+selectedAccountName:" Global"}`,context:`${selectedAccountName||"Toate paginile"}, ultimele 30 zile`,data:{total:totalAll,peak_day:peak.day,peak_total:peak.total||0,avg_daily:activeDays>0?Math.round(totalAll/activeDays):0,total_positive:tlData.reduce((s,d)=>s+(d.positive||0),0),total_negative:tlData.reduce((s,d)=>s+(d.negative||0),0),total_trolls:tlData.reduce((s,d)=>s+(d.trolls||0),0),active_days:activeDays,total_days:tlData.length,days:tlData.map(d=>d.day)}});}} />
            </div>
          </div>
          <div className="rounded-2xl p-5" style={card}>
            <h3 className="font-semibold text-white mb-4">Top Comentatori Globali</h3>
            <CommentersTable commenters={allCommenters} onSelectCommenter={(id,name)=>setSelectedCommenter({id,name})} />
          </div>
        </div>
      )}

      {activeSection === "pages" && !selectedPage && (
        <div className="space-y-3">
          {pages.length === 0 && <div className="text-center py-12 text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>Nicio pagină{selectedAccountName ? ` pentru ${selectedAccountName}` : ""}</div>}
          {pages.map(page => (
            <div key={page.id} className="rounded-2xl p-5 cursor-pointer transition-all" style={card}
              onClick={() => setSelectedPage(page)}
              onMouseEnter={e=>e.currentTarget.style.border="1px solid rgba(99,102,241,0.4)"}
              onMouseLeave={e=>e.currentTarget.style.border="1px solid rgba(255,255,255,0.08)"}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-white">{page.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={pageTypeStyles[page.page_type]}>{page.page_type}</span>
                </div>
                <div className="flex gap-6 text-right">
                  <div><p className="text-lg font-bold text-white">{page.post_count}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>postări</p></div>
                  <div><p className="text-lg font-bold" style={{color:"#a5b4fc"}}>{page.comment_count}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>comentarii</p></div>
                  <div><p className="text-lg font-bold" style={{color:"#86efac"}}>{page.total_reactions}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>reacții</p></div>
                  <div className="flex items-center" style={{color:"rgba(255,255,255,0.3)"}}>→</div>
                </div>
              </div>
              <div className="mt-3"><MiniBar value={page.total_reactions} max={maxReactions} color="#6366f1" /></div>
            </div>
          ))}
        </div>
      )}

      {activeSection === "pages" && selectedPage && <PageDetail page={selectedPage} onBack={() => setSelectedPage(null)} />}

      {activeSection === "campanii" && !selectedCampaign && (
        <div className="space-y-3">
          {campaigns.length===0&&<div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.2)"}}>Nicio campanie creată</div>}
          {campaigns.map(c=>{
            const progress=c.total_pages>0?Math.round((c.posted_pages/c.total_pages)*100):0;
            return(
              <div key={c.id} className="rounded-2xl p-5 cursor-pointer transition-all" style={card}
                onClick={()=>setSelectedCampaign(c)}
                onMouseEnter={e=>e.currentTarget.style.border="1px solid rgba(99,102,241,0.4)"}
                onMouseLeave={e=>e.currentTarget.style.border="1px solid rgba(255,255,255,0.08)"}>
                <div className="flex justify-between items-start mb-3">
                  <div><h3 className="font-semibold text-white">{c.title}</h3><p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.3)"}}>{new Date(c.created_at).toLocaleDateString('ro-RO')}</p></div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={c.status==='active'?{background:"rgba(34,197,94,0.15)",color:"#86efac"}:{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.4)"}}>{c.status}</span>
                    <span style={{color:"rgba(255,255,255,0.3)"}}>→</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {[{label:"Total",value:c.total_pages,color:"white"},{label:"Publicate",value:c.posted_pages,color:"#86efac"},{label:"Aprobate",value:c.approved_pages,color:"#a5b4fc"},{label:"În așteptare",value:c.pending_pages,color:"rgba(255,255,255,0.4)"}].map((stat,i)=>(
                    <div key={i} className="rounded-xl p-2 text-center" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}><p className="text-lg font-bold" style={{color:stat.color}}>{stat.value}</p><p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{stat.label}</p></div>
                  ))}
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{color:"rgba(255,255,255,0.3)"}}><span>Progress</span><span>{progress}%</span></div>
                  <div className="w-full rounded-full h-1.5" style={{background:"rgba(255,255,255,0.06)"}}><div className="h-1.5 rounded-full" style={{width:`${progress}%`,background:"linear-gradient(135deg, #6366f1, #8b5cf6)"}}></div></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeSection === "campanii" && selectedCampaign && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setSelectedCampaign(null)} className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>← Înapoi</button>
            <h3 className="text-xl font-bold text-white">{selectedCampaign.title}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[{label:"Total pagini",value:selectedCampaign.total_pages,color:"white"},{label:"Publicate",value:selectedCampaign.posted_pages,color:"#86efac"},{label:"Aprobate",value:selectedCampaign.approved_pages,color:"#a5b4fc"},{label:"În așteptare",value:selectedCampaign.pending_pages,color:"rgba(255,255,255,0.4)"}].map((stat,i)=>(
              <StatCard key={i} label={stat.label} value={stat.value} color={stat.color} />
            ))}
          </div>
          <CampaignDetail campaignId={selectedCampaign.id} campaignTitle={selectedCampaign.title} />
        </div>
      )}

      {activeSection === "teme" && (
        <div>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-bold text-white">🎯 Teme de Atac</h3>
              <p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.35)"}}>
                {themesData?.analyzed || 0} comentarii analizate
                {themesData?.remaining > 0 && ` · ${themesData?.remaining} neanalizate`}
              </p>
            </div>
            <div className="flex gap-2">
              {themesData?.remaining > 0 && (
                <button onClick={async () => {
                  setAnalyzingThemes(true);
                  await axios.post(`${API}/themes/analyze`);
                  const poll = setInterval(async () => {
                    const s = await axios.get(`${API}/themes/analyze-status`);
                    setAnalyzeProgress(s.data);
                    if (s.data.finished) {
                      clearInterval(poll);
                      setAnalyzingThemes(false);
                      fetchThemes();
                    }
                  }, 5000);
                }} disabled={analyzingThemes}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium disabled:opacity-50" style={btnPrimary}>
                  {analyzingThemes
                    ? analyzeProgress?.batch === "consolidare"
                      ? "🧠 Consolidare..."
                      : `Batch ${analyzeProgress?.batch}/${analyzeProgress?.total_batches}...`
                    : `🔍 Analizează ${themesData?.remaining} noi`}
                </button>
              )}
              <button onClick={fetchThemes} className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>🔄</button>
            </div>
          </div>

          {themesLoading && <div className="text-sm py-8 text-center" style={{color:"rgba(255,255,255,0.25)"}}>Se încarcă...</div>}

          {/* Modal contra-narativa */}
          {counterNarrative && (
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{background:"rgba(0,0,0,0.85)"}}>
              <div className="rounded-2xl p-6 w-full flex flex-col" style={{background:"rgba(15,15,26,1)",border:"1px solid rgba(34,197,94,0.3)",maxWidth:"600px",maxHeight:"85vh",overflow:"auto"}}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">🛡️ Contra-narativă</h3>
                    <p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.4)"}}>{counterNarrative.theme}</p>
                  </div>
                  <button onClick={() => setCounterNarrative(null)}
                    className="px-3 py-1.5 rounded-xl text-xs" style={{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)"}}>
                    Închide
                  </button>
                </div>
                <div className="text-sm whitespace-pre-wrap" style={{color:"rgba(255,255,255,0.85)",lineHeight:"1.7"}}>
                  {counterNarrative.text}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            {themesData?.themes?.map((theme, i) => (
              <div key={i} className="rounded-2xl p-4" style={{
                background: theme.fake_news ? "rgba(239,68,68,0.05)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${theme.fake_news ? "rgba(239,68,68,0.3)" : theme.tone === "agresiv" ? "rgba(249,115,22,0.2)" : theme.tone === "dezinformare" ? "rgba(234,179,8,0.2)" : "rgba(255,255,255,0.08)"}`
              }}>
                <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-white">{theme.theme}</span>
                    {theme.fake_news && <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{background:"rgba(239,68,68,0.2)",color:"#fca5a5"}}>🚨 FAKE NEWS</span>}
                    <span className="text-xs px-2 py-0.5 rounded-full" style={
                      theme.tone === "agresiv" ? {background:"rgba(249,115,22,0.15)",color:"#fdba74"} :
                      theme.tone === "dezinformare" ? {background:"rgba(234,179,8,0.15)",color:"#fde047"} :
                      theme.tone === "ironic" ? {background:"rgba(139,92,246,0.15)",color:"#c4b5fd"} :
                      {background:"rgba(99,102,241,0.15)",color:"#a5b4fc"}
                    }>{theme.tone}</span>
                  </div>
                  <span className="text-sm font-bold" style={{color:"#fca5a5"}}>{theme.total_frequency} comentarii</span>
                </div>
                <div className="rounded-full h-1.5 mb-3" style={{background:"rgba(255,255,255,0.06)"}}>
                  <div className="h-1.5 rounded-full" style={{
                    width:`${Math.min(100,(theme.total_frequency/(themesData?.themes?.[0]?.total_frequency||1))*100)}%`,
                    background: theme.fake_news ? "linear-gradient(135deg,#ef4444,#dc2626)" :
                                theme.tone === "agresiv" ? "linear-gradient(135deg,#f97316,#ea580c)" :
                                theme.tone === "dezinformare" ? "linear-gradient(135deg,#eab308,#ca8a04)" :
                                "linear-gradient(135deg,#6366f1,#8b5cf6)"
                  }}></div>
                </div>
                {theme.descriere && <p className="text-xs mb-2" style={{color:"rgba(255,255,255,0.5)"}}>{theme.descriere}</p>}
                {theme.subteme?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {theme.subteme.map((s,j) => (
                      <span key={j} className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.35)"}}>{s}</span>
                    ))}
                  </div>
                )}
                {theme.examples?.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {theme.examples.slice(0,3).map((ex,j) => (
                      <p key={j} className="text-xs italic" style={{color:"rgba(255,255,255,0.4)"}}>"{ex}"</p>
                    ))}
                  </div>
                )}
                <button onClick={async () => {
                  setGeneratingCounter(theme.theme);
                  try {
                    const res = await axios.post(`${API}/themes/${encodeURIComponent(theme.theme)}/counter-narrative`, {
                      examples: theme.examples, tone: theme.tone, fake_news: theme.fake_news, frequency: theme.total_frequency
                    });
                    setCounterNarrative({theme: theme.theme, text: res.data.counter_narrative});
                  } catch(e) { console.error(e); }
                  setGeneratingCounter(null);
                }} disabled={generatingCounter === theme.theme}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium disabled:opacity-50"
                  style={{background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.2)",color:"#86efac"}}>
                  {generatingCounter === theme.theme ? "Se generează..." : "🛡️ Contra-narativă"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === "coordonare" && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-bold text-white">🕵️ Analiză Coordonare</h3>
              <p className="text-xs mt-0.5" style={{color:"rgba(255,255,255,0.35)"}}>Detectare comportament coordonat și conturi suspecte</p>
            </div>
            <button onClick={fetchCoordination} className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnGray}>
              🔄 Reîncarcă
            </button>
          </div>

          {/* Sub-taburi */}
          <div className="flex gap-1 mb-5 p-1 rounded-xl flex-wrap" style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)"}}>
            {[
              {id:"supporters", label:"⭐ Susținători"},
              {id:"summary", label:"🚫 Negativi"},
              {id:"bursts", label:"⚡ Valuri"},
              {id:"posts", label:"📄 Postări Atacate"},
            ].map(t => (
              <button key={t.id} onClick={() => setCoordTab(t.id)}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={coordTab === t.id ? btnPrimary : {color:"rgba(255,255,255,0.4)"}}>
                {t.label}
              </button>
            ))}
          </div>

          {coordLoading && <div className="text-sm py-8 text-center" style={{color:"rgba(255,255,255,0.3)"}}>Se analizează...</div>}

          {/* Useri Suspecți */}
          {!coordLoading && coordTab === "summary" && coordData && (
            <div className="space-y-3">
              {coordData.summary.suspicious_users?.length === 0 && (
                <div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.25)"}}>Niciun user suspect detectat</div>
              )}
              {coordData.summary.suspicious_users?.map((user, i) => (
                <div key={i} className="rounded-2xl p-4" style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${user.behavior === 'agresiv' ? 'rgba(239,68,68,0.3)' : user.behavior === 'coordonat' ? 'rgba(234,179,8,0.3)' : 'rgba(255,255,255,0.08)'}`}}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <button className="font-semibold text-sm text-white hover:text-indigo-300"
                        onClick={() => setSelectedCommenter({id: user.from_id, name: user.from_name})}>
                        {user.from_name}
                      </button>
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={
                        user.behavior === 'agresiv' ? {background:"rgba(239,68,68,0.15)",color:"#fca5a5"} :
                        user.behavior === 'coordonat' ? {background:"rgba(234,179,8,0.15)",color:"#fde047"} :
                        user.behavior === 'sustinator_activ' ? {background:"rgba(34,197,94,0.15)",color:"#86efac"} :
                        {background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.4)"}
                      }>{user.behavior}</span>
                      {user.is_blocked
                        ? <span className="ml-1 text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(249,115,22,0.15)",color:"#fdba74"}}>🚫 blocat</span>
                        : <span className="ml-1 text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(34,197,94,0.08)",color:"rgba(134,239,172,0.6)"}}>● activ</span>
                      }
                      {user.last_seen && <span className="ml-1 text-xs" style={{color:"rgba(255,255,255,0.25)"}}>· ultima activitate: {new Date(user.last_seen.includes('Z') ? user.last_seen : user.last_seen.replace(' ','T')+'Z').toLocaleDateString('ro-RO')}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>Scor suspiciune:</span>
                      <span className="font-bold text-sm" style={{color: user.suspicion_score > 60 ? "#fca5a5" : user.suspicion_score > 30 ? "#fde047" : "#86efac"}}>
                        {user.suspicion_score}/100
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    {[
                      {label:"Total comentarii", value: user.total, color:"white"},
                      {label:"Negative", value: user.negative, color:"#fca5a5"},
                      {label:"Pagini targetate", value: user.pages_targeted, color:"#a5b4fc"},
                      {label:"Postări targetate", value: user.posts_targeted, color:"#c4b5fd"},
                    ].map((s,j) => (
                      <div key={j} className="text-center">
                        <p className="text-sm font-bold" style={{color:s.color}}>{s.value}</p>
                        <p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {/* Bar scor */}
                  <div className="mt-2 rounded-full h-1" style={{background:"rgba(255,255,255,0.06)"}}>
                    <div className="h-1 rounded-full transition-all" style={{
                      width:`${user.suspicion_score}%`,
                      background: user.suspicion_score > 60 ? "linear-gradient(135deg,#ef4444,#dc2626)" :
                                  user.suspicion_score > 30 ? "linear-gradient(135deg,#eab308,#ca8a04)" :
                                  "linear-gradient(135deg,#22c55e,#16a34a)"
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sustinatori Activi */}
          {!coordLoading && coordTab === "supporters" && coordData && (
            <div className="space-y-3">
              {coordData.supporters?.supporters?.length === 0 && (
                <div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.25)"}}>Niciun susținător activ detectat</div>
              )}
              {coordData.supporters?.supporters?.map((user, i) => (
                <div key={i} className="rounded-2xl p-4" style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${user.on_whitelist ? 'rgba(234,179,8,0.3)' : 'rgba(34,197,94,0.2)'}`}}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button className="font-semibold text-sm text-white hover:text-indigo-300"
                        onClick={() => setSelectedCommenter({id: user.from_id, name: user.from_name})}>
                        {user.from_name}
                      </button>
                      {user.on_whitelist
                        ? <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(234,179,8,0.15)",color:"#fde047"}}>⭐ whitelist</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(34,197,94,0.1)",color:"#86efac"}}>susținător</span>
                      }
                      {user.is_blocked && <span className="text-xs px-2 py-0.5 rounded-full" style={{background:"rgba(249,115,22,0.15)",color:"#fdba74"}}>🚫 blocat</span>}
                    </div>
                    <span className="text-xs" style={{color:"rgba(255,255,255,0.25)"}}>
                      {user.last_seen ? new Date(user.last_seen.includes('Z') ? user.last_seen : user.last_seen.replace(' ','T')+'Z').toLocaleDateString('ro-RO') : ''}
                    </span>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    {[
                      {label:"Total comentarii", value:user.total, color:"white"},
                      {label:"Susținător", value:user.sustinator, color:"#86efac"},
                      {label:"Negative", value:user.negative, color:"#fca5a5"},
                      {label:"Pagini active", value:user.pages_targeted, color:"#a5b4fc"},
                      {label:"Postări comentate", value:user.posts_targeted, color:"#c4b5fd"},
                    ].map((s,j) => (
                      <div key={j} className="text-center">
                        <p className="text-sm font-bold" style={{color:s.color}}>{s.value}</p>
                        <p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Valuri Activitate */}
          {!coordLoading && coordTab === "bursts" && coordData && (
            <div className="space-y-3">
              {coordData.bursts.bursts?.length === 0 && (
                <div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.25)"}}>Niciun val de activitate detectat</div>
              )}
              {coordData.bursts.bursts?.map((burst, i) => (
                <div key={i} className="rounded-2xl p-4" style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${burst.suspicion_score > 50 ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)'}`}}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-semibold text-sm text-white">🕐 {burst.time_bucket}:00</span>
                      <span className="ml-2 text-xs" style={{color:"rgba(255,255,255,0.35)"}}>interval 1h</span>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-lg" style={{
                      background: burst.suspicion_score > 50 ? "rgba(239,68,68,0.15)" : "rgba(234,179,8,0.15)",
                      color: burst.suspicion_score > 50 ? "#fca5a5" : "#fde047"
                    }}>Suspiciune: {burst.suspicion_score}/100</span>
                  </div>
                  <div className="flex gap-4 flex-wrap mb-3">
                    {[
                      {label:"Useri unici", value:burst.unique_users, color:"white"},
                      {label:"Total comentarii", value:burst.total_comments, color:"#a5b4fc"},
                      {label:"Troli", value:burst.trolls, color:"#fca5a5"},
                      {label:"Critici", value:burst.critics, color:"#fde047"},
                      {label:"Susținători", value:burst.supporters, color:"#86efac"},
                    ].map((s,j) => (
                      <div key={j} className="text-center">
                        <p className="text-sm font-bold" style={{color:s.color}}>{s.value}</p>
                        <p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {burst.user_names?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {burst.user_names.slice(0,8).map((name,j) => {
                        const userStatus = burst.users_status?.[name];
                        return (
                          <span key={j} className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                            style={userStatus?.is_blocked
                              ? {background:"rgba(249,115,22,0.1)",color:"#fdba74",border:"1px solid rgba(249,115,22,0.2)"}
                              : {background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)"}}>
                            {userStatus?.is_blocked ? "🚫" : "●"} {name}
                          </span>
                        );
                      })}
                      {burst.user_names.length > 8 && <span className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>+{burst.user_names.length-8}</span>}
                    </div>
                  )}
                  {burst.top_posts?.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold" style={{color:"rgba(255,255,255,0.3)"}}>POSTĂRI ATACATE:</p>
                      {burst.top_posts.map((post, j) => (
                        <div key={j} className="rounded-lg px-3 py-2" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)"}}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium" style={{color:"#a5b4fc"}}>{post.page_name}</span>
                            <div className="flex gap-2 text-xs">
                              <span style={{color:"#fca5a5"}}>🚫 {post.trolls} troli</span>
                              <span style={{color:"#fde047"}}>⚠️ {post.critics} critici</span>
                              <span style={{color:"rgba(255,255,255,0.3)"}}>💬 {post.comment_count} total</span>
                            </div>
                          </div>
                          <p className="text-xs" style={{color:"rgba(255,255,255,0.4)"}}>{post.message_short}...</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}


          {/* Postari Atacate */}
          {!coordLoading && coordTab === "posts" && coordData && (
            <div className="space-y-4">
              {coordData.postsAttack?.posts?.length === 0 && (
                <div className="text-center py-12 text-sm" style={{color:"rgba(255,255,255,0.25)"}}>Nicio postare atacată detectată</div>
              )}
              {coordData.postsAttack?.posts?.map((post, i) => (
                <div key={i} className="rounded-2xl p-4" style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(239,68,68,0.2)"}}>
                  <div className="mb-3">
                    <span className="text-xs font-medium" style={{color:"#a5b4fc"}}>{post.page_name}</span>
                    <p className="text-sm mt-1" style={{color:"rgba(255,255,255,0.7)"}}>{post.post_message_short}...</p>
                  </div>
                  <div className="flex gap-4 flex-wrap mb-4">
                    {[
                      {label:"Total comentarii", value:post.total_comments, color:"white"},
                      {label:"Troli", value:post.trolls, color:"#fca5a5"},
                      {label:"Critici", value:post.critics, color:"#fde047"},
                      {label:"Susținători", value:post.supporters, color:"#86efac"},
                      {label:"Useri unici", value:post.unique_users, color:"#a5b4fc"},
                    ].map((s,j) => (
                      <div key={j} className="text-center">
                        <p className="text-sm font-bold" style={{color:s.color}}>{s.value}</p>
                        <p className="text-xs" style={{color:"rgba(255,255,255,0.3)"}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {post.hours?.length > 0 && (
                    <>
                      <HoursDisplay hours={post.hours} />
                      {/* Grafic heatmap ore */}
                      <div className="mt-3">
                        <p className="text-xs font-semibold mb-2" style={{color:"rgba(255,255,255,0.3)"}}>DISTRIBUȚIE ORE (susținători vs negativi):</p>
                        <div className="flex items-end gap-0.5" style={{height:"60px"}}>
                          {(() => {
                            const maxVal = Math.max(...post.hours.map(h => h.total), 1);
                            return post.hours.map((h, j) => {
                              const negative = h.trolls + h.critics;
                              const height = Math.round((h.total / maxVal) * 100);
                              const negHeight = Math.round((negative / maxVal) * 100);
                              const supHeight = Math.round((h.supporters / maxVal) * 100);
                              return (
                                <div key={j} className="flex flex-col justify-end flex-1" style={{height:"60px",minWidth:"4px"}}>
                                  <div style={{height:`${supHeight}%`,background:"rgba(34,197,94,0.6)",borderRadius:"2px 2px 0 0"}}></div>
                                  <div style={{height:`${negHeight}%`,background:"rgba(239,68,68,0.7)",borderRadius:"2px 2px 0 0"}}></div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                        {/* Axa X - zile + ore */}
                        <div className="flex gap-0.5 mt-0.5">
                          {post.hours.map((h, j) => {
                            // Verificam daca e schimbare de zi
                            const prevDay = j > 0 ? post.hours[j-1].hour?.split(' ')[0] : null;
                            const currDay = h.hour?.split(' ')[0];
                            const isNewDay = currDay !== prevDay;
                            const hourPart = h.hour?.split(' ')[1]?.substring(0,5) || h.hour?.substring(0,5);
                            return (
                              <div key={j} className="flex-1 text-center" style={{minWidth:"4px",position:"relative"}}>
                                {isNewDay && (
                                  <p style={{color:"rgba(99,102,241,1)",fontSize:"9px",whiteSpace:"nowrap",position:"absolute",left:0,top:0,fontWeight:"bold"}}>
                                    {currDay}
                                  </p>
                                )}
                                {j % 4 === 0 && (
                                  <p className="text-xs mt-3" style={{color:"rgba(255,255,255,0.6)",fontSize:"9px"}}>
                                    {hourPart}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs flex items-center gap-1"><span style={{background:"rgba(239,68,68,0.7)",width:"10px",height:"10px",borderRadius:"2px",display:"inline-block"}}></span><span style={{color:"rgba(255,255,255,0.4)"}}>Negativi</span></span>
                          <span className="text-xs flex items-center gap-1"><span style={{background:"rgba(34,197,94,0.6)",width:"10px",height:"10px",borderRadius:"2px",display:"inline-block"}}></span><span style={{color:"rgba(255,255,255,0.4)"}}>Susținători</span></span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
